/**
 * Generic "hydrate-once, listen-for-changes" cache helper.
 *
 * The media-plan-builder feature has three independent in-memory
 * caches (estimate versions, saved plans, SF↔estimate bindings) that
 * each implemented the same lifecycle by hand:
 *
 *   • `hydrate()` — fetch from Snowflake, dedup concurrent calls,
 *     mark hydrated even on failure so the UI can render in degraded
 *     mode with a banner.
 *   • `isHydrated()` — true once the first fetch settled (either way).
 *   • `getError()` — last fetch error, or null.
 *   • `retry()` — re-runs `hydrate()` after a previous failure.
 *   • `onChange(cb)` — subscribe to hydrate-status flips so UI can
 *     re-render its failure banner.
 *
 * The boilerplate added up — about 60 lines per file. This factory
 * centralises the pattern. The caller passes a fetch + apply pair;
 * the factory owns the lifecycle state and exposes the same API
 * surface the three storage modules used to declare by hand.
 *
 * Why a factory (rather than a class):
 *   The exports each storage module surfaces are functions, not a
 *   class instance, because that's the public API the rest of the
 *   codebase already consumes. Re-exporting bound methods from a
 *   factory keeps the migration to this helper a one-line swap per
 *   public function.
 */

/**
 * Public API surface returned by `createCacheHydration`. Each storage
 * module re-exports these as named functions matching its old API
 * (`hydrateEstimateVersions`, `isHydrated`, etc.) so call sites in
 * the rest of the app don't have to change.
 */
export interface CacheHydration {
  /**
   * Fire the initial hydrate (no-op if already done) or share an
   * in-flight call. Resolves once the fetch settles either way.
   */
  hydrate: () => Promise<void>;
  /**
   * Has the first hydrate ever finished? Returns true even on
   * failure so the rest of the app can stop showing its loading
   * screen and surface a banner instead.
   */
  isHydrated: () => boolean;
  /** Last fetch error string, or null on success. */
  getError: () => string | null;
  /**
   * Re-run hydrate after a previous failure. Safe no-op while a
   * hydrate is in flight or already succeeded.
   */
  retry: () => Promise<void>;
  /**
   * Subscribe to hydrate-status flips. Returns an unsubscribe fn.
   * UI components use this to re-render their failure banner /
   * "retry" button when hydrate state flips success ↔ error.
   */
  onChange: (cb: () => void) => () => void;
  /**
   * Fire the change listeners *without* a hydrate. Useful after a
   * write helper has mutated the underlying cache directly (e.g.
   * `Map.set` after a successful POST) so subscribers re-render
   * against the new state without paying for a full re-fetch.
   */
  notifyChange: () => void;
  /**
   * Reset the hydrated flag without firing a refetch. Use when an
   * external write means the cache is now incomplete and you want
   * the very next `hydrate()` call to do work even on a previously
   * successful boot (the standard `hydrate()` is a no-op once
   * hydrated). Pairs with an immediate `await hydrate()` afterwards.
   */
  reset: () => void;
}

export interface CacheHydrationOptions<T> {
  /**
   * Human-readable label used in error messages and console logs
   * (e.g. "estimate versions", "saved plans"). Keep lowercase + plural
   * — it ends up in user-facing copy like "Failed to load {x}.".
   */
  resourceName: string;
  /** Fetcher that returns the list of items to populate the cache. */
  fetch: () => Promise<T[]>;
  /**
   * Apply the fetched items to whatever cache the caller owns. Called
   * synchronously inside the hydrate promise, so callers can clear +
   * repopulate their Map / Set without observers seeing a torn state.
   */
  apply: (items: T[]) => void;
}

export function createCacheHydration<T>(
  opts: CacheHydrationOptions<T>,
): CacheHydration {
  let hydrated = false;
  let hydratePromise: Promise<void> | null = null;
  let hydrateError: string | null = null;
  const listeners = new Set<() => void>();

  function notify(): void {
    listeners.forEach(cb => { try { cb(); } catch { /* ignore */ } });
  }

  async function hydrate(): Promise<void> {
    if (hydrated && !hydrateError) return;
    if (hydratePromise) return hydratePromise;

    hydratePromise = (async () => {
      try {
        const items = await opts.fetch();
        opts.apply(items);
        hydrated = true;
        hydrateError = null;
      } catch (err: any) {
        // Log + record the error so the UI can render a banner with
        // retry. Marking hydrated = true unblocks the boot screen
        // even on failure (degraded mode).
        // eslint-disable-next-line no-console
        console.error(`hydrate ${opts.resourceName} failed`, err);
        hydrated = true;
        hydrateError =
          err?.response?.data?.error
          || err?.message
          || `Failed to load ${opts.resourceName}.`;
      } finally {
        hydratePromise = null;
        notify();
      }
    })();

    return hydratePromise;
  }

  return {
    hydrate,
    isHydrated: () => hydrated,
    getError: () => hydrateError,
    retry: async () => {
      if (hydratePromise) return hydratePromise;
      if (hydrated && !hydrateError) return;
      hydrated = false;
      hydrateError = null;
      return hydrate();
    },
    onChange: (cb: () => void) => {
      listeners.add(cb);
      return () => { listeners.delete(cb); };
    },
    notifyChange: notify,
    reset: () => {
      hydrated = false;
      hydratePromise = null;
      hydrateError = null;
    },
  };
}
